import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Spinner from 'components/common/Spinner';
import { getUserDetails, UserDetailsResponse } from 'services/admin/accountsAnalysisService';
import { useTheme } from 'contexts/ThemeContext';
import { getErrorMessage } from 'utils/errors';
import {
  UserCircle,
  CreditCard,
  Activity,
  Globe,
  X,
  AlertCircle,
  Inbox,
  User,
  CheckCircle,
  Lock,
} from 'lucide-react';

interface UserDetailsModalProps {
  userId: string;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ userId, onClose }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<UserDetailsResponse | null>(null);

  // Theme tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const textPrimary = isDark ? '#E2E8F0' : '#1E293B';
  const textSecondary = isDark ? '#90A1B9' : '#64748B';
  const borderColor = isDark ? '#1D293D' : '#F1F5F9';
  const headerBg = isDark ? '#1D293D' : '#F8FAFC';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getUserDetails(userId);
        setData(response);
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, t('admin.accountsAnalysis.errors.fetchDetailsFailed')));
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, onClose, t]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    switch (status.toLowerCase()) {
      case 'active':
        return { background: isDark ? '#14532D' : '#DCFCE7', color: isDark ? '#4ADE80' : '#15803D' };
      case 'trial':
        return { background: isDark ? '#581C87' : '#F3E8FF', color: isDark ? '#C084FC' : '#7E22CE' };
      case 'expired':
        return { background: isDark ? '#7F1D1D' : '#FEE2E2', color: isDark ? '#F87171' : '#B91C1C' };
      default:
        return { background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' };
    }
  };

  // Stat card color sets
  const statCardColors = {
    blue: {
      bg: isDark ? '#1C398E' : '#EFF6FF',
      border: isDark ? '#2B5BCC' : '#BFDBFE',
      value: isDark ? '#51A2FF' : '#2563EB',
      label: isDark ? '#7BBDFF' : '#1D4ED8',
    },
    green: {
      bg: isDark ? '#14532D' : '#F0FDF4',
      border: isDark ? '#166534' : '#BBF7D0',
      value: isDark ? '#4ADE80' : '#16A34A',
      label: isDark ? '#86EFAC' : '#15803D',
    },
    purple: {
      bg: isDark ? '#581C87' : '#FAF5FF',
      border: isDark ? '#6B21A8' : '#E9D5FF',
      value: isDark ? '#C084FC' : '#9333EA',
      label: isDark ? '#D8B4FE' : '#7E22CE',
    },
    teal: {
      bg: isDark ? '#134E4A' : '#F0FDFA',
      border: isDark ? '#115E59' : '#99F6E4',
      value: isDark ? '#2DD4BF' : '#0D9488',
      label: isDark ? '#5EEAD4' : '#0F766E',
    },
    amber: {
      bg: isDark ? '#78350F' : '#FFFBEB',
      border: isDark ? '#92400E' : '#FDE68A',
      value: isDark ? '#FBBF24' : '#D97706',
      label: isDark ? '#FCD34D' : '#B45309',
    },
    indigo: {
      bg: isDark ? '#312E81' : '#EEF2FF',
      border: isDark ? '#3730A3' : '#C7D2FE',
      value: isDark ? '#818CF8' : '#4F46E5',
      label: isDark ? '#A5B4FC' : '#4338CA',
    },
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center"
      style={{
        background: isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)',
        backdropFilter: 'blur(10px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal */}
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden mx-4"
        style={{ background: mainBg, borderRadius: '16px', boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ background: headerBg, borderBottom: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: iconContainerBg }}
            >
              <UserCircle size={20} style={{ color: iconColor }} />
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: textPrimary }}>
                {t('admin.accountsAnalysis.userDetails') || 'User Details'}
              </h2>
              <p className="text-xs" style={{ color: subtitleColor }}>
                {t('admin.accountsAnalysis.completeUserInfo') || 'Complete user information and connected accounts'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? '#314158' : '#F1F5F9';
              e.currentTarget.style.color = textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = textSecondary;
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className={`p-6 overflow-y-auto max-h-[calc(90vh-80px)] ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="large" text={t('common.loading') || 'Loading...'} />
            </div>
          ) : !data ? (
            <div className="text-center py-20">
              <AlertCircle size={48} style={{ color: textSecondary, margin: '0 auto', marginBottom: '12px' }} />
              <p className="text-sm" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.noDataFound') || 'No data found'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* User Info Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info Card */}
                <div
                  className="rounded-lg p-4"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: textPrimary }}>
                    <UserCircle size={16} style={{ color: iconColor }} />
                    {t('admin.accountsAnalysis.basicInfo') || 'Basic Information'}
                  </h3>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                      style={{ background: isDark ? '#314158' : '#E5E7EB' }}
                    >
                      {data.user.profilePicture ? (
                        <img src={data.user.profilePicture} alt={data.user.name} className="w-full h-full object-cover" />
                      ) : (
                        <User size={28} style={{ color: textSecondary }} />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div>
                        <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.name') || 'Name'}</p>
                        <p className="text-sm font-medium" style={{ color: textPrimary }}>{data.user.name}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.email') || 'Email'}</p>
                        <p className="text-sm font-medium" style={{ color: textPrimary }}>{data.user.email}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.phone') || 'Phone'}</p>
                        <p className="text-sm font-medium" style={{ color: textPrimary }}>{data.user.phone || '-'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={data.user.isActive
                            ? { background: isDark ? '#14532D' : '#DCFCE7', color: isDark ? '#4ADE80' : '#15803D' }
                            : { background: isDark ? '#7F1D1D' : '#FEE2E2', color: isDark ? '#F87171' : '#B91C1C' }
                          }
                        >
                          {data.user.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {data.user.isEmailVerified && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                            style={{ background: isDark ? '#1C398E' : '#DBEAFE', color: isDark ? '#51A2FF' : '#1D4ED8' }}
                          >
                            <CheckCircle size={12} style={{ marginRight: '4px' }} />
                            Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className="mt-4 pt-4 grid grid-cols-2 gap-4"
                    style={{ borderTop: `1px solid ${cardBorder}` }}
                  >
                    <div>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.joinDate') || 'Join Date'}</p>
                      <p className="text-sm font-medium" style={{ color: textPrimary }}>{formatDate(data.user.joinDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.lastLogin') || 'Last Login'}</p>
                      <p className="text-sm font-medium" style={{ color: textPrimary }}>{formatDate(data.user.lastLogin)}</p>
                    </div>
                  </div>
                </div>

                {/* Subscription & Payment Card */}
                <div
                  className="rounded-lg p-4"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: textPrimary }}>
                    <CreditCard size={16} style={{ color: iconColor }} />
                    {t('admin.accountsAnalysis.subscriptionPayment') || 'Subscription & Payment'}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.plan') || 'Plan'}</span>
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold"
                        style={getStatusBadgeStyle(data.subscription.type)}
                      >
                        {data.subscription.type}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.status') || 'Status'}</span>
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold"
                        style={getStatusBadgeStyle(data.subscription.status)}
                      >
                        {data.subscription.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.billingCycle') || 'Billing Cycle'}</span>
                      <span className="text-sm font-medium" style={{ color: textPrimary }}>{data.subscription.billingCycle || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.endDate') || 'End Date'}</span>
                      <span className="text-sm font-medium" style={{ color: textPrimary }}>{formatDate(data.subscription.endDate)}</span>
                    </div>
                    <div style={{ borderTop: `1px solid ${cardBorder}`, paddingTop: '16px' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.totalPaid') || 'Total Paid'}</span>
                        <span className="text-sm font-bold" style={{ color: isDark ? '#4ADE80' : '#16A34A' }}>{formatCurrency(data.payment.totalPaid)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.lastPayment') || 'Last Payment'}</span>
                        <span className="text-sm font-medium" style={{ color: textPrimary }}>{formatDate(data.payment.lastPaymentDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {[
                  { value: data.summary.totalAccounts, label: t('admin.accountsAnalysis.totalAccounts') || 'Total Accounts', colors: statCardColors.blue },
                  { value: data.summary.activeAccounts, label: t('admin.accountsAnalysis.activeAccounts') || 'Active Accounts', colors: statCardColors.green },
                  { value: data.summary.totalJobs, label: t('admin.accountsAnalysis.totalJobs') || 'Total Jobs', colors: statCardColors.purple },
                  { value: data.summary.completedJobs, label: t('admin.accountsAnalysis.completedJobs') || 'Completed Jobs', colors: statCardColors.teal },
                  { value: data.summary.totalRules, label: t('admin.accountsAnalysis.totalRules') || 'Total Rules', colors: statCardColors.amber },
                  { value: data.summary.activeRules, label: t('admin.accountsAnalysis.activeRules') || 'Active Rules', colors: statCardColors.indigo },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg p-3 text-center"
                    style={{ background: stat.colors.bg, border: `1px solid ${stat.colors.border}` }}
                  >
                    <p className="text-lg font-bold" style={{ color: stat.colors.value }}>{stat.value}</p>
                    <p className="text-xs" style={{ color: stat.colors.label }}>{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Activity Totals */}
              {data.summary.activityTotals && (
                <div
                  className="rounded-lg p-4"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: textPrimary }}>
                    <Activity size={16} style={{ color: iconColor }} />
                    {t('admin.accountsAnalysis.activityTotals') || 'Activity Totals'}
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: isDark ? '#F87171' : '#EF4444' }}>{data.summary.activityTotals.likes}</p>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.likes') || 'Likes'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: isDark ? '#51A2FF' : '#3B82F6' }}>{data.summary.activityTotals.comments}</p>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.comments') || 'Comments'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: isDark ? '#4ADE80' : '#22C55E' }}>{data.summary.activityTotals.follows}</p>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.follows') || 'Follows'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: isDark ? '#C084FC' : '#A855F7' }}>{data.summary.activityTotals.messages}</p>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.messages') || 'Messages'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: isDark ? '#FBBF24' : '#F59E0B' }}>{data.summary.activityTotals.stories}</p>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.stories') || 'Stories'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ color: isDark ? '#2DD4BF' : '#14B8A6' }}>{data.summary.activityTotals.posts}</p>
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.posts') || 'Posts'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Connected Accounts */}
              <div
                className="rounded-lg overflow-hidden"
                style={{ border: `1px solid ${cardBorder}` }}
              >
                <div
                  className="px-4 py-3"
                  style={{ background: headerBg, borderBottom: `1px solid ${cardBorder}` }}
                >
                  <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: textPrimary }}>
                    <Globe size={16} style={{ color: iconColor }} />
                    {t('admin.accountsAnalysis.connectedAccounts') || 'Connected Accounts'} ({data.accounts.length})
                  </h3>
                </div>
                <div>
                  {data.accounts.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <Inbox size={36} style={{ color: textSecondary, margin: '0 auto', marginBottom: '8px' }} />
                      <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.noAccountsConnected') || 'No accounts connected'}</p>
                    </div>
                  ) : (
                    data.accounts.map((account, index) => (
                      <div
                        key={account._id}
                        className="px-4 py-4"
                        style={{ borderTop: index > 0 ? `1px solid ${borderColor}` : undefined }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                              style={{ background: isDark ? '#314158' : '#E5E7EB' }}
                            >
                              {account.profilePictureUrl ? (
                                <img src={account.profilePictureUrl} alt={account.username} className="w-full h-full object-cover" />
                              ) : (
                                <Globe size={20} style={{ color: textSecondary }} />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-semibold" style={{ color: textPrimary }}>@{account.username}</p>
                              <p className="text-xs capitalize" style={{ color: textSecondary }}>{account.platform} • {account.profile.accountType}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {account.isLocked ? (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                    style={{ background: isDark ? '#7F1D1D' : '#FEE2E2', color: isDark ? '#F87171' : '#B91C1C' }}
                                  >
                                    <Lock size={10} style={{ marginRight: '4px' }} />
                                    {t('admin.accountsAnalysis.locked') || 'Locked'}
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                    style={account.isActive
                                      ? { background: isDark ? '#14532D' : '#DCFCE7', color: isDark ? '#4ADE80' : '#15803D' }
                                      : { background: isDark ? '#314158' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' }
                                    }
                                  >
                                    {account.isActive ? (t('admin.accountsAnalysis.active') || 'Active') : (t('admin.accountsAnalysis.inactive') || 'Inactive')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.followers') || 'Followers'}</p>
                            <p className="text-sm font-semibold" style={{ color: textPrimary }}>{account.profile.followersCount.toLocaleString()}</p>
                          </div>
                        </div>

                        {/* Account Activity Stats */}
                        {account.activityStats && (
                          <div
                            className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2 p-3 rounded-lg"
                            style={{ background: isDark ? '#0F172B' : '#F9FAFB' }}
                          >
                            <div className="text-center">
                              <p className="text-sm font-bold" style={{ color: isDark ? '#F87171' : '#EF4444' }}>{account.activityStats.likes}</p>
                              <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.likes') || 'Likes'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold" style={{ color: isDark ? '#51A2FF' : '#3B82F6' }}>{account.activityStats.comments}</p>
                              <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.comments') || 'Comments'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold" style={{ color: isDark ? '#4ADE80' : '#22C55E' }}>{account.activityStats.follows}</p>
                              <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.follows') || 'Follows'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold" style={{ color: isDark ? '#C084FC' : '#A855F7' }}>{account.activityStats.messages}</p>
                              <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.messages') || 'Messages'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold" style={{ color: isDark ? '#FBBF24' : '#F59E0B' }}>{account.activityStats.stories}</p>
                              <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.stories') || 'Stories'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-bold" style={{ color: isDark ? '#2DD4BF' : '#14B8A6' }}>{account.activityStats.posts}</p>
                              <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.posts') || 'Posts'}</p>
                            </div>
                          </div>
                        )}

                        {/* Account Stats */}
                        <div className="mt-3 grid grid-cols-4 gap-4">
                          <div className="text-center">
                            <p className="text-sm font-bold" style={{ color: textPrimary }}>{account.jobs.total}</p>
                            <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.jobs') || 'Jobs'}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-bold" style={{ color: isDark ? '#4ADE80' : '#16A34A' }}>{account.jobs.successRate.toFixed(1)}%</p>
                            <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.successRate') || 'Success Rate'}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-bold" style={{ color: textPrimary }}>{account.rules.total}</p>
                            <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.rules') || 'Rules'}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-bold" style={{ color: isDark ? '#51A2FF' : '#2563EB' }}>{account.rules.active}</p>
                            <p className="text-xs" style={{ color: textSecondary }}>{t('admin.accountsAnalysis.activeRulesShort') || 'Active'}</p>
                          </div>
                        </div>

                        {/* Action Settings */}
                        {account.rules.settings.length > 0 && (
                          <div
                            className="mt-4 pt-4"
                            style={{ borderTop: `1px solid ${borderColor}` }}
                          >
                            <p className="text-xs font-semibold mb-2" style={{ color: titleColor }}>{t('admin.accountsAnalysis.actionSettings') || 'Action Settings'}</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {account.rules.settings.map((setting) => (
                                <div
                                  key={setting.actionId}
                                  className="px-2 py-1.5 rounded text-xs"
                                  style={{
                                    background: setting.isActive
                                      ? (isDark ? '#14532D' : '#F0FDF4')
                                      : (isDark ? '#1D293D' : '#F9FAFB'),
                                    border: `1px solid ${setting.isActive
                                      ? (isDark ? '#166534' : '#BBF7D0')
                                      : cardBorder}`,
                                  }}
                                >
                                  <p className="font-medium truncate" style={{ color: textPrimary }}>{setting.friendlyName}</p>
                                  <p style={{ color: textSecondary }}>{setting.limitUtilized}/{setting.limit}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserDetailsModal;
